import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import TestPushDialog, { type TestPushData } from '../../components/Connect/TestPushDialog';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  useGetNotificationChannelQuery,
  useCreateNotificationChannelMutation,
  useUpdateNotificationChannelMutation,
  useTestNotificationChannelMutation,
} from '../../services/connectApi';
import type { NotificationChannel } from '../../models';
import { API_URL } from '../../services/api';

type ChannelType = 'push' | 'email' | 'sms' | 'whatsapp';

interface PushConfig {
  firebase_service_key_json?: string;
  firebase_project_id?: string;
  apns_key_id?: string;
  apns_team_id?: string;
  apns_key_file?: string;
}

interface EmailConfig {
  smtp_host?: string;
  smtp_port?: string;
  smtp_username?: string;
  smtp_password?: string;
  use_tls?: boolean;
  default_from_email?: string;
  email_backend?: string;
}

interface SmsConfig {
  provider?: 'twilio' | 'aws_sns' | 'other';
  account_sid?: string;
  auth_token?: string;
  from_number?: string;
  region?: string;
  api_key?: string;
}

interface WhatsAppConfig {
  provider?: 'twilio' | 'facebook' | 'other';
  account_sid?: string;
  auth_token?: string;
  phone_number_id?: string;
  from_phone_number?: string;
  access_token?: string;
}

const ChannelForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [channel, setChannel] = useState<Partial<NotificationChannel & {app?: string}>>({
    name: '',
    type: 'push',
    description: '',
    configuration: {},
    is_active: true,
    app: '',
  });
  
  const [apps, setApps] = useState<any[]>([]);
  const [isLoadingApps, setIsLoadingApps] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [testMessage, setTestMessage] = useState<{type: 'success' | 'error'; message: string} | null>(null);
  
  // Test push notification specific fields
  const [templates, setTemplates] = useState<any[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isTestDialogOpen, setIsTestDialogOpen] = useState(false);

  const { data: existingChannel, isLoading } = useGetNotificationChannelQuery(
    id as string,
    { skip: !isEditMode }
  );
  
  const [createChannel] = useCreateNotificationChannelMutation();
  const [updateChannel] = useUpdateNotificationChannelMutation();
  const [testChannel] = useTestNotificationChannelMutation();

  useEffect(() => {
    if (isEditMode && existingChannel) {
      setChannel(existingChannel);
    }
  }, [isEditMode, existingChannel]);

  useEffect(() => {
    const fetchApps = async () => {
      if (channel.type === 'push') {
        setIsLoadingApps(true);
        try {
          const response = await fetch(`${API_URL}/app/apps/`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          const data = await response.json();
          const appsList = Array.isArray(data.results) ? data.results : data;
          setApps(appsList);
        } catch (error) {
          console.error('Failed to fetch apps:', error);
        } finally {
          setIsLoadingApps(false);
        }
      }
    };
    
    fetchApps();
  }, [channel.type]);
  
  // Fetch notification templates for testing
  useEffect(() => {
    if (isEditMode && channel.type === 'push') {
      const fetchTemplates = async () => {
        setIsLoadingTemplates(true);
        try {
          const response = await fetch(`${API_URL}/connect/templates/?page=1&page_size=100`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          const data = await response.json();
          setTemplates(data.results || []);
        } catch (error) {
          console.error('Failed to fetch templates:', error);
        } finally {
          setIsLoadingTemplates(false);
        }
      };
      
      fetchTemplates();
    }
  }, [isEditMode, channel.type]);

  const handleInputChange = (field: keyof NotificationChannel, value: any) => {
    setChannel((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field if it exists
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleConfigChange = (config: Record<string, any>) => {
    setChannel((prev) => ({
      ...prev,
      configuration: config,
    }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!channel.name) {
      newErrors.name = 'Name is required';
    }
    
    if (!channel.type) {
      newErrors.type = 'Channel type is required';
    }
    
    // Channel-specific validations
    if (channel.type === 'push') {
      // Validate App field is selected for push notifications
      if (!channel.app) {
        newErrors.app = 'App is required for push notifications';
      }
      
      const pushConfig = channel.configuration as PushConfig;
      if (!pushConfig.firebase_service_key_json) {
        newErrors.firebase_service_key_json = 'Firebase Service Key JSON is required for push notifications';
      } else {
        // Validate if it's a valid JSON
        try {
          const parsed = JSON.parse(pushConfig.firebase_service_key_json);
          if (!parsed.type || parsed.type !== 'service_account') {
            newErrors.firebase_service_key_json = 'Invalid service account JSON format';
          }
          if (!parsed.project_id) {
            newErrors.firebase_service_key_json = 'Service account JSON must contain project_id';
          }
          if (!parsed.private_key) {
            newErrors.firebase_service_key_json = 'Service account JSON must contain private_key';
          }
          if (!parsed.client_email) {
            newErrors.firebase_service_key_json = 'Service account JSON must contain client_email';
          }
        } catch (error) {
          newErrors.firebase_service_key_json = 'Invalid JSON format';
        }
      }
    } else if (channel.type === 'email') {
      const emailConfig = channel.configuration as EmailConfig;
      if (!emailConfig.smtp_host) {
        newErrors.smtp_host = 'SMTP Host is required for email notifications';
      }
      if (!emailConfig.default_from_email) {
        newErrors.default_from_email = 'Default From Email is required';
      }
    } else if (channel.type === 'sms') {
      const smsConfig = channel.configuration as SmsConfig;
      if (!smsConfig.provider) {
        newErrors.sms_provider = 'SMS Provider is required';
      }
    } else if (channel.type === 'whatsapp') {
      const whatsappConfig = channel.configuration as WhatsAppConfig;
      if (!whatsappConfig.provider) {
        newErrors.whatsapp_provider = 'WhatsApp Provider is required';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    try {
      if (isEditMode) {
        await updateChannel({
          id: id as string,
          data: channel,
        }).unwrap();
      } else {
        await createChannel(channel).unwrap();
      }
      navigate('/connect#channels');
    } catch (error) {
      console.error('Failed to save channel:', error);
      setErrors({
        submit: 'Failed to save channel. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenTestDialog = () => {
    if (!isEditMode) {
      setTestMessage({ 
        type: 'error', 
        message: 'Please save the channel first before testing' 
      });
      return;
    }
    
    setIsTestDialogOpen(true);
  };
  
  const handleTestChannel = async (testData: TestPushData) => {
    try {
      setIsSubmitting(true);
      setTestMessage(null);
      
      // Prepare test data based on channel type
      const apiData: any = {};
      
      if (channel.type === 'push') {
        // For push notifications, include FCM token and template details
        if (testData.fcm_token) {
          apiData.fcm_token = testData.fcm_token;
        }
        
        if (testData.use_custom_template) {
          apiData.test_title = testData.test_title;
          apiData.test_body = testData.test_body;
        } else if (testData.template_id) {
          apiData.template_id = testData.template_id;
        }
      }
      
      const result = await testChannel({
        id: id as string,
        data: apiData
      }).unwrap();
      
      setTestMessage({
        type: 'success',
        message: result.message || 'Channel test completed successfully!'
      });
      
      setIsTestDialogOpen(false);
    } catch (error: any) {
      console.error('Failed to test channel:', error);
      setTestMessage({
        type: 'error',
        message: error.data?.detail || 'Failed to test channel. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderConfigurationFields = () => {
    switch (channel.type) {
      case 'push':
        return renderPushConfigFields();
      case 'email':
        return renderEmailConfigFields();
      case 'sms':
        return renderSmsConfigFields();
      case 'whatsapp':
        return renderWhatsAppConfigFields();
      default:
        return null;
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const jsonContent = e.target?.result as string;
        // Try to parse to validate it's a valid JSON
        JSON.parse(jsonContent);
        
        // Update the configuration
        const config = channel.configuration as PushConfig || {};
        handleConfigChange({ ...config, firebase_service_key_json: jsonContent });
        
        // Clear any errors
        if (errors.firebase_service_key_json) {
          setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.firebase_service_key_json;
            return newErrors;
          });
        }
      } catch (error) {
        setErrors((prev) => ({
          ...prev,
          firebase_service_key_json: 'Invalid JSON file. Please upload a valid service account JSON file.',
        }));
      }
    };
    reader.readAsText(file);
  };

  const renderPushConfigFields = () => {
    const config = channel.configuration as PushConfig || {};
    
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="subtitle1">Firebase Configuration (for Android/Web)</Typography>
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ mb: 2 }}>
            <input
              type="file"
              accept=".json"
              style={{ display: 'none' }}
              id="service-key-upload"
              onChange={handleFileUpload}
              ref={fileInputRef}
            />
            <label htmlFor="service-key-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadFileIcon />}
                onClick={() => fileInputRef.current?.click()}
              >
                Upload Service Key JSON
              </Button>
            </label>
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Upload your Firebase service account JSON file. You can download this from your Firebase project settings &gt; Service accounts.
            </Typography>
          </Box>
          <TextField
            label="Firebase Service Key JSON"
            fullWidth
            required
            multiline
            rows={8}
            value={config.firebase_service_key_json || ''}
            onChange={(e) => handleConfigChange({ ...config, firebase_service_key_json: e.target.value })}
            error={!!errors.firebase_service_key_json}
            helperText={errors.firebase_service_key_json || 'Or paste your Firebase service account JSON here.'}
            placeholder={`{
  "type": "service_account",
  "project_id": "your-project-id",
  "private_key_id": "key-id",
  "private_key": "-----BEGIN PRIVATE KEY-----\\n...\\n-----END PRIVATE KEY-----\\n",
  "client_email": "firebase-adminsdk-xxxxx@your-project-id.iam.gserviceaccount.com",
  "client_id": "client-id",
  "auth_uri": "https://accounts.google.com/o/oauth2/auth",
  "token_uri": "https://oauth2.googleapis.com/token",
  "auth_provider_x509_cert_url": "https://www.googleapis.com/oauth2/v1/certs",
  "client_x509_cert_url": "https://www.googleapis.com/robot/v1/metadata/x509/..."
}`}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Firebase Project ID"
            fullWidth
            value={config.firebase_project_id || ''}
            onChange={(e) => handleConfigChange({ ...config, firebase_project_id: e.target.value })}
            helperText="Optional: Override project ID from service key JSON"
          />
        </Grid>
        
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1">Apple Push Notification Service (for iOS)</Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="APNs Key ID"
            fullWidth
            value={config.apns_key_id || ''}
            onChange={(e) => handleConfigChange({ ...config, apns_key_id: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="APNs Team ID"
            fullWidth
            value={config.apns_team_id || ''}
            onChange={(e) => handleConfigChange({ ...config, apns_team_id: e.target.value })}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="APNs Key File (Base64 encoded p8 file)"
            fullWidth
            multiline
            rows={4}
            value={config.apns_key_file || ''}
            onChange={(e) => handleConfigChange({ ...config, apns_key_file: e.target.value })}
          />
        </Grid>
      </Grid>
    );
  };

  const renderEmailConfigFields = () => {
    const config = channel.configuration as EmailConfig || {};
    
    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            label="SMTP Host"
            fullWidth
            required
            value={config.smtp_host || ''}
            onChange={(e) => handleConfigChange({ ...config, smtp_host: e.target.value })}
            error={!!errors.smtp_host}
            helperText={errors.smtp_host || ''}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="SMTP Port"
            fullWidth
            type="number"
            value={config.smtp_port || ''}
            onChange={(e) => handleConfigChange({ ...config, smtp_port: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="SMTP Username"
            fullWidth
            value={config.smtp_username || ''}
            onChange={(e) => handleConfigChange({ ...config, smtp_username: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="SMTP Password"
            fullWidth
            type="password"
            value={config.smtp_password || ''}
            onChange={(e) => handleConfigChange({ ...config, smtp_password: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Switch
                checked={config.use_tls === undefined ? true : config.use_tls}
                onChange={(e) => handleConfigChange({ ...config, use_tls: e.target.checked })}
              />
            }
            label="Use TLS"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Default From Email"
            fullWidth
            required
            value={config.default_from_email || ''}
            onChange={(e) => handleConfigChange({ ...config, default_from_email: e.target.value })}
            error={!!errors.default_from_email}
            helperText={errors.default_from_email || ''}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Email Backend</InputLabel>
            <Select
              value={config.email_backend || 'smtp'}
              label="Email Backend"
              onChange={(e) => handleConfigChange({ ...config, email_backend: e.target.value })}
            >
              <MenuItem value="smtp">SMTP</MenuItem>
              <MenuItem value="ses">Amazon SES</MenuItem>
              <MenuItem value="sendgrid">SendGrid</MenuItem>
              <MenuItem value="mailgun">Mailgun</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    );
  };

  const renderSmsConfigFields = () => {
    const config = channel.configuration as SmsConfig || {};
    
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControl fullWidth required error={!!errors.sms_provider}>
            <InputLabel>SMS Provider</InputLabel>
            <Select
              value={config.provider || ''}
              label="SMS Provider"
              onChange={(e) => handleConfigChange({ ...config, provider: e.target.value })}
            >
              <MenuItem value="twilio">Twilio</MenuItem>
              <MenuItem value="aws_sns">AWS SNS</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
            {errors.sms_provider && <FormHelperText>{errors.sms_provider}</FormHelperText>}
          </FormControl>
        </Grid>
        
        {config.provider === 'twilio' && (
          <>
            <Grid item xs={12} md={6}>
              <TextField
                label="Twilio Account SID"
                fullWidth
                required
                value={config.account_sid || ''}
                onChange={(e) => handleConfigChange({ ...config, account_sid: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Twilio Auth Token"
                fullWidth
                required
                type="password"
                value={config.auth_token || ''}
                onChange={(e) => handleConfigChange({ ...config, auth_token: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="From Phone Number"
                fullWidth
                required
                value={config.from_number || ''}
                onChange={(e) => handleConfigChange({ ...config, from_number: e.target.value })}
              />
            </Grid>
          </>
        )}
        
        {config.provider === 'aws_sns' && (
          <>
            <Grid item xs={12} md={6}>
              <TextField
                label="AWS Region"
                fullWidth
                required
                value={config.region || ''}
                onChange={(e) => handleConfigChange({ ...config, region: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="API Key"
                fullWidth
                required
                type="password"
                value={config.api_key || ''}
                onChange={(e) => handleConfigChange({ ...config, api_key: e.target.value })}
              />
            </Grid>
          </>
        )}
        
        {config.provider === 'other' && (
          <Grid item xs={12}>
            <TextField
              label="API Configuration (JSON)"
              fullWidth
              multiline
              rows={6}
              placeholder='{"api_key": "your_api_key", "endpoint": "https://example.com/api/sms"}'
              value={JSON.stringify(config, null, 2)}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value);
                  handleConfigChange(parsed);
                } catch (error) {
                  // Ignore parse errors while typing
                }
              }}
            />
          </Grid>
        )}
      </Grid>
    );
  };

  const renderWhatsAppConfigFields = () => {
    const config = channel.configuration as WhatsAppConfig || {};
    
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControl fullWidth required error={!!errors.whatsapp_provider}>
            <InputLabel>WhatsApp Provider</InputLabel>
            <Select
              value={config.provider || ''}
              label="WhatsApp Provider"
              onChange={(e) => handleConfigChange({ ...config, provider: e.target.value })}
            >
              <MenuItem value="twilio">Twilio</MenuItem>
              <MenuItem value="facebook">Facebook Business API</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
            {errors.whatsapp_provider && <FormHelperText>{errors.whatsapp_provider}</FormHelperText>}
          </FormControl>
        </Grid>
        
        {config.provider === 'twilio' && (
          <>
            <Grid item xs={12} md={6}>
              <TextField
                label="Twilio Account SID"
                fullWidth
                required
                value={config.account_sid || ''}
                onChange={(e) => handleConfigChange({ ...config, account_sid: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Twilio Auth Token"
                fullWidth
                required
                type="password"
                value={config.auth_token || ''}
                onChange={(e) => handleConfigChange({ ...config, auth_token: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="From Phone Number"
                fullWidth
                required
                value={config.from_phone_number || ''}
                onChange={(e) => handleConfigChange({ ...config, from_phone_number: e.target.value })}
              />
            </Grid>
          </>
        )}
        
        {config.provider === 'facebook' && (
          <>
            <Grid item xs={12} md={6}>
              <TextField
                label="Phone Number ID"
                fullWidth
                required
                value={config.phone_number_id || ''}
                onChange={(e) => handleConfigChange({ ...config, phone_number_id: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Access Token"
                fullWidth
                required
                type="password"
                value={config.access_token || ''}
                onChange={(e) => handleConfigChange({ ...config, access_token: e.target.value })}
              />
            </Grid>
          </>
        )}
        
        {config.provider === 'other' && (
          <Grid item xs={12}>
            <TextField
              label="API Configuration (JSON)"
              fullWidth
              multiline
              rows={6}
              placeholder='{"api_key": "your_api_key", "endpoint": "https://example.com/api/whatsapp"}'
              value={JSON.stringify(config, null, 2)}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value);
                  handleConfigChange(parsed);
                } catch (error) {
                  // Ignore parse errors while typing
                }
              }}
            />
          </Grid>
        )}
      </Grid>
    );
  };

  if (isEditMode && isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/connect#channels')}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h5" component="h1">
          {isEditMode ? 'Edit Notification Channel' : 'Create Notification Channel'}
        </Typography>
      </Box>

      {errors.submit && (
        <Box sx={{ bgcolor: 'error.light', color: 'error.contrastText', p: 2, borderRadius: 1, mb: 3 }}>
          <Typography variant="body1">{errors.submit}</Typography>
        </Box>
      )}

      <Paper elevation={3}>
        <Box sx={{ p: 3 }}>
          <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
            {channel.type === 'push' ? 
              'Configure a push notification channel for sending messages to mobile devices. You need to select an app to determine which devices will receive these notifications.' : 
              channel.type === 'email' ? 
              'Configure an email notification channel for sending emails to users.' :
              channel.type === 'sms' ?
              'Configure an SMS notification channel for sending text messages to users.' :
              'Configure a WhatsApp notification channel for sending WhatsApp messages to users.'
            }
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Channel Name"
                fullWidth
                required
                value={channel.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                error={!!errors.name}
                helperText={errors.name || ''}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required error={!!errors.type}>
                <InputLabel>Channel Type</InputLabel>
                <Select
                  value={channel.type || ''}
                  label="Channel Type"
                  onChange={(e) => handleInputChange('type', e.target.value as ChannelType)}
                  disabled={isEditMode} // Can't change channel type after creation
                >
                  <MenuItem value="push">Push Notification</MenuItem>
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="sms">SMS</MenuItem>
                  <MenuItem value="whatsapp">WhatsApp</MenuItem>
                </Select>
                {errors.type && <FormHelperText>{errors.type}</FormHelperText>}
              </FormControl>
            </Grid>
            
            {channel.type === 'push' && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required error={!!errors.app}>
                  <InputLabel>Select App</InputLabel>
                  <Select
                    value={channel.app || ''}
                    label="Select App"
                    onChange={(e) => setChannel(prev => ({ ...prev, app: e.target.value }))}
                    disabled={isEditMode && Boolean(channel.app)} // Can't change app after creation if already set
                  >
                    {apps.map((app) => (
                      <MenuItem key={app.id} value={app.id}>
                        {app.name} ({app.type})
                      </MenuItem>
                    ))}
                    {isLoadingApps && <MenuItem disabled>Loading apps...</MenuItem>}
                    {!isLoadingApps && apps.length === 0 && <MenuItem disabled>No apps available</MenuItem>}
                  </Select>
                  {errors.app && <FormHelperText error>{errors.app}</FormHelperText>}
                  <FormHelperText>
                    {!errors.app && "Push notifications require an associated app to determine which devices to notify"}
                  </FormHelperText>
                </FormControl>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={channel.description || ''}
                onChange={(e) => handleInputChange('description', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={channel.is_active === undefined ? true : channel.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </Box>

        <Divider />

        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Channel Configuration
          </Typography>
          <Card variant="outlined">
            <CardContent>
              {renderConfigurationFields()}
            </CardContent>
          </Card>
        </Box>

        {testMessage && (
          <Box sx={{ 
            p: 2, 
            mx: 3, 
            mb: 2, 
            borderRadius: 1, 
            display: 'flex',
            alignItems: 'center',
            bgcolor: testMessage.type === 'success' ? 'success.light' : 'error.light', 
            color: testMessage.type === 'success' ? 'success.contrastText' : 'error.contrastText' 
          }}>
            {testMessage.type === 'success' ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '12px'}}>
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '12px'}}>
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            )}
            <Typography variant="body1">{testMessage.message}</Typography>
          </Box>
        )}

        <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between' }}>
          <Box>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/connect#channels')}
              sx={{ mr: 2 }}
            >
              Cancel
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {isEditMode && (
              <Button
                variant="outlined"
                color="info"
                startIcon={isSubmitting ? <CircularProgress size={20} /> : <SendIcon />}
                onClick={handleOpenTestDialog}
                disabled={isSubmitting}
                title={`Test ${channel.type} channel configuration`}
              >
                {isSubmitting ? 'Testing...' : `Test ${channel.type === 'push' ? 'Push' : channel.type === 'email' ? 'Email' : channel.type === 'sms' ? 'SMS' : 'WhatsApp'} Channel`}
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              startIcon={isSubmitting && !testMessage ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting && !testMessage ? 'Saving...' : 'Save Channel'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Test Push Notification Dialog */}
      <TestPushDialog
        open={isTestDialogOpen}
        onClose={() => setIsTestDialogOpen(false)}
        onSubmit={handleTestChannel}
        isSubmitting={isSubmitting}
        templates={templates}
        isLoadingTemplates={isLoadingTemplates}
        channelType={channel.type || 'push'}
      />
    </Box>
  );
};

export default ChannelForm;
